import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, resolveMedia, isVideoUrl } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Plus, Trash, UploadSimple, GearSix, Image as ImageIcon, Megaphone, MusicNotes, CurrencyDollar, UserPlus, UserCircle, FacebookLogo, InstagramLogo, TiktokLogo, ShareNetwork } from "@phosphor-icons/react";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { Header, In, IconBtn, Modal } from "./shared";

export default function SettingsTab() {
  const { settings, reloadSettings } = useSettings();
  const [images, setImages] = useState([]);
  const [slideshow, setSlideshow] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [divisions, setDivisions] = useState([]);
  const [teamCounts, setTeamCounts] = useState({});
  const [pendingDelete, setPendingDelete] = useState(null);
  const [heroFit, setHeroFit] = useState("cover");
  const [uploading, setUploading] = useState(false);
  const [savingDiv, setSavingDiv] = useState(false);
  const [divisionAges, setDivisionAges] = useState({});
  const [savingAges, setSavingAges] = useState(false);
  const [savingWp, setSavingWp] = useState(false);
  const [anns, setAnns] = useState([]);
  const [annUrl, setAnnUrl] = useState("");
  const [uploadingAnn, setUploadingAnn] = useState(false);
  const [savingAnn, setSavingAnn] = useState(false);
  const [musicUrl, setMusicUrl] = useState("");
  const [musicTracks, setMusicTracks] = useState([]);
  const [musicEnabled, setMusicEnabled] = useState(false);
  const [uploadingMusic, setUploadingMusic] = useState(false);
  const [savingMusic, setSavingMusic] = useState(false);
  const MAX_TRACKS = 8;
  const emptyPayments = { enabled: false, instructions: "", zelle: { enabled: false, label: "", qr_url: "" }, venmo: { enabled: false, label: "", qr_url: "" } };
  const [payments, setPayments] = useState(emptyPayments);
  const [savingPay, setSavingPay] = useState(false);
  const [uploadingQr, setUploadingQr] = useState("");
  const emptyHero = { season_title: "", status_text: "", tagline: "", overlay_color: "#000000", primary_cta_text: "", primary_cta_link: "", secondary_cta_text: "", secondary_cta_link: "" };
  const [hero, setHero] = useState(emptyHero);
  const [savingHero, setSavingHero] = useState(false);
  const [allowDupJerseys, setAllowDupJerseys] = useState(false);
  const [savingDup, setSavingDup] = useState(false);
  const [defaultFee, setDefaultFee] = useState("");
  const [waiverText, setWaiverText] = useState("");
  const [savingCompliance, setSavingCompliance] = useState(false);
  const emptySocial = { facebook: "", instagram: "", tiktok: "" };
  const [social, setSocial] = useState(emptySocial);
  const [savingSocial, setSavingSocial] = useState(false);

  useEffect(() => {
    setImages(settings.hero_images && settings.hero_images.length ? settings.hero_images : (settings.hero_image_url ? [settings.hero_image_url] : []));
    setSlideshow(!!settings.slideshow);
    setDivisions(settings.divisions || []);
    setDivisionAges(settings.division_ages || {});
    setHeroFit(settings.hero_fit || "cover");
    setAllowDupJerseys(!!settings.allow_duplicate_jerseys);
    setDefaultFee(settings.default_player_fee != null ? String(settings.default_player_fee) : "");
    setWaiverText(settings.waiver_text || "");
    setAnns(settings.announcements || []);    setMusicEnabled(!!settings.music_enabled);
    setMusicTracks(
      settings.music_tracks && settings.music_tracks.length
        ? settings.music_tracks
        : (settings.music_url ? [settings.music_url] : [])
    );
    if (settings.payments) {
      setPayments({
        enabled: !!settings.payments.enabled,
        instructions: settings.payments.instructions || "",
        zelle: { enabled: false, label: "", qr_url: "", ...(settings.payments.zelle || {}) },
        venmo: { enabled: false, label: "", qr_url: "", ...(settings.payments.venmo || {}) },
      });
    }
    setSocial({ facebook: "", instagram: "", tiktok: "", ...(settings.social || {}) });
    if (settings.hero) {
      setHero({
        season_title: settings.hero.season_title || "",
        status_text: settings.hero.status_text || "",
        tagline: settings.hero.tagline || "",
        overlay_color: settings.hero.overlay_color || "#000000",
        primary_cta_text: settings.hero.primary_cta_text ?? "Register Now",
        primary_cta_link: settings.hero.primary_cta_link ?? "/register",
        secondary_cta_text: settings.hero.secondary_cta_text ?? "View Schedule",
        secondary_cta_link: settings.hero.secondary_cta_link ?? "/schedule",
      });
    }
  }, [settings]);

  useEffect(() => {
    api.get("/teams").then((r) => {
      const counts = {};
      (r.data || []).forEach((t) => {
        const d = (t.division || "Open").trim();
        counts[d] = (counts[d] || 0) + 1;
      });
      setTeamCounts(counts);
    }).catch(() => {});
  }, []);

  const requestDeleteDivision = (i) => {
    const name = (divisions[i] || "").trim();
    const count = name ? (teamCounts[name] || 0) : 0;
    if (count > 0) {
      setPendingDelete({ index: i, name, count });
    } else {
      setDivisions((prev) => prev.filter((_, idx) => idx !== i));
    }
  };

  const confirmDeleteDivision = () => {
    if (!pendingDelete) return;
    setDivisions((prev) => prev.filter((_, idx) => idx !== pendingDelete.index));
    setPendingDelete(null);
  };

  const setPayMethod = (method, key, value) => setPayments((p) => ({ ...p, [method]: { ...(p[method] || {}), [key]: value } }));

  const uploadQr = async (method, file) => {
    if (!file) return;
    setUploadingQr(method);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/settings/wallpaper", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPayMethod(method, "qr_url", res.data.url);
      toast.success("QR uploaded — remember to Save");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingQr(""); }
  };
  const saveHero = async () => {
    if (!hero.tagline.trim()) { toast.error("Tagline can't be empty"); return; }
    setSavingHero(true);
    try {
      await api.put("/settings", { hero: {
        season_title: hero.season_title.trim(),
        status_text: hero.status_text.trim(),
        tagline: hero.tagline.trim(),
        overlay_color: hero.overlay_color || "#000000",
        primary_cta_text: hero.primary_cta_text.trim(),
        primary_cta_link: hero.primary_cta_link.trim(),
        secondary_cta_text: hero.secondary_cta_text.trim(),
        secondary_cta_link: hero.secondary_cta_link.trim(),
      } });
      toast.success("Homepage hero updated");
      reloadSettings();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingHero(false); }
  };

  const savePayments = async () => {
    setSavingPay(true);
    try {
      await api.put("/settings", { payments });      toast.success("Payment settings saved");
      reloadSettings();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingPay(false); }
  };

  const saveSocial = async () => {
    setSavingSocial(true);
    try {
      await api.put("/settings", { social: {
        facebook: social.facebook.trim(),
        instagram: social.instagram.trim(),
        tiktok: social.tiktok.trim(),
      } });
      toast.success("Social links saved");
      reloadSettings();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingSocial(false); }
  };

  const uploadAnnFile = async (file) => {
    if (!file) return;
    setUploadingAnn(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/settings/wallpaper", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnns((prev) => [...prev, { url: res.data.url, caption: "" }]);
      toast.success("Media uploaded — remember to Save");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingAnn(false); }
  };
  const addAnnUrl = () => {
    const u = annUrl.trim();
    if (!u) return;
    setAnns((prev) => [...prev, { url: u, caption: "" }]);
    setAnnUrl("");
  };
  const setAnnCaption = (i, v) => setAnns((prev) => prev.map((a, idx) => (idx === i ? { ...a, caption: v } : a)));
  const removeAnn = (i) => setAnns((prev) => prev.filter((_, idx) => idx !== i));
  const moveAnn = (i, dir) => setAnns((prev) => {
    const j = i + dir;
    if (j < 0 || j >= prev.length) return prev;
    const copy = [...prev];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });
  const saveAnnouncements = async () => {
    setSavingAnn(true);
    try {
      await api.put("/settings", { announcements: anns.filter((a) => a.url).map((a) => ({ url: a.url, caption: a.caption || "" })) });
      toast.success("Announcements saved");
      reloadSettings();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingAnn(false); }
  };

  const uploadMusicFile = async (file) => {
    if (!file) return;
    if (musicTracks.length >= MAX_TRACKS) { toast.error(`You can add up to ${MAX_TRACKS} tracks`); return; }
    setUploadingMusic(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/settings/wallpaper", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMusicTracks((prev) => [...prev, res.data.url]);
      toast.success("Track added — remember to Save");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingMusic(false); }
  };
  const addMusicUrl = () => {
    const u = musicUrl.trim();
    if (!u) return;
    if (musicTracks.length >= MAX_TRACKS) { toast.error(`You can add up to ${MAX_TRACKS} tracks`); return; }
    setMusicTracks((prev) => [...prev, u]);
    setMusicUrl("");
  };
  const removeMusicTrack = (i) => setMusicTracks((prev) => prev.filter((_, idx) => idx !== i));
  const saveMusic = async () => {
    setSavingMusic(true);
    try {
      const tracks = musicTracks.map((t) => t.trim()).filter(Boolean).slice(0, MAX_TRACKS);
      await api.put("/settings", { music_tracks: tracks, music_url: tracks[0] || "", music_enabled: musicEnabled });
      toast.success("Background music saved");
      reloadSettings();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingMusic(false); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/settings/wallpaper", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImages((prev) => [...prev, res.data.url]);
      toast.success("Image uploaded — remember to Save");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const addUrl = () => {
    const u = newUrl.trim();
    if (!u) return;
    setImages((prev) => [...prev, u]);
    setNewUrl("");
  };
  const removeImage = (i) => setImages((prev) => prev.filter((_, idx) => idx !== i));
  const moveImage = (i, dir) => setImages((prev) => {
    const j = i + dir;
    if (j < 0 || j >= prev.length) return prev;
    const copy = [...prev];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });

  const saveWallpaper = async () => {
    setSavingWp(true);
    try {
      await api.put("/settings", { hero_images: images, hero_image_url: images[0] || "", slideshow, hero_fit: heroFit });
      toast.success("Wallpaper saved");
      reloadSettings();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingWp(false); }
  };

  const saveDupJerseys = async (value) => {
    setAllowDupJerseys(value);
    setSavingDup(true);
    try {
      await api.put("/settings", { allow_duplicate_jerseys: value });
      toast.success(value ? "Duplicate jersey numbers allowed" : "Duplicate jersey numbers blocked");
      reloadSettings();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setAllowDupJerseys(!value); }
    finally { setSavingDup(false); }
  };

  const saveCompliance = async () => {
    const fee = parseFloat(defaultFee);
    if (isNaN(fee) || fee < 0) { toast.error("Enter a valid default fee"); return; }
    if (!waiverText.trim()) { toast.error("Waiver text can't be empty"); return; }
    setSavingCompliance(true);
    try {
      const prevVersion = settings.waiver_version;
      const res = await api.put("/settings", { default_player_fee: fee, waiver_text: waiverText });
      const newVersion = res.data?.waiver_version;
      if (prevVersion != null && newVersion != null && newVersion > prevVersion) {
        toast.warning(`Waiver updated to v${newVersion} — all players must re-sign before they're eligible to play.`, { duration: 6000 });
      } else {
        toast.success("Fee & waiver settings saved");
      }
      reloadSettings();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingCompliance(false); }
  };

  const setAge = (div, key, value) => setDivisionAges((prev) => ({ ...prev, [div]: { ...(prev[div] || {}), [key]: value === "" ? "" : Math.max(0, parseInt(value, 10) || 0) } }));

  const saveAges = async () => {
    const cleaned = {};
    for (const d of (divisions || [])) {
      const name = (d || "").trim();
      if (!name) continue;
      const r = divisionAges[name] || {};
      const min = r.min === "" || r.min == null ? null : Number(r.min);
      const max = r.max === "" || r.max == null ? null : Number(r.max);
      if (min != null && max != null && min > max) { toast.error(`Minimum age can't be greater than maximum for ${name}`); return; }
      if (min != null || max != null) cleaned[name] = { min, max };
    }
    setSavingAges(true);
    try {
      await api.put("/settings", { division_ages: cleaned });
      toast.success("Division age rules saved");
      reloadSettings();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingAges(false); }
  };

  const saveDivisions = async () => {
    const cleaned = divisions.map((d) => d.trim()).filter(Boolean);
    if (!cleaned.length) { toast.error("Add at least one division"); return; }
    if (new Set(cleaned.map((d) => d.toLowerCase())).size !== cleaned.length) { toast.error("Division names must be unique"); return; }
    setSavingDiv(true);
    try {
      await api.put("/settings", { divisions: cleaned });
      toast.success("Divisions saved");
      reloadSettings();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingDiv(false); }
  };

  return (
    <div data-testid="settings-tab" className="max-w-3xl">
      <Header title="Site Settings" />

      {/* Homepage Hero Text */}
      <div className="border border-white/10 bg-[#121212] p-6 mb-6" data-testid="hero-settings">
        <div className="flex items-center gap-2 mb-4"><GearSix size={20} className="text-[#FF5722]" /><h3 className="font-display text-2xl uppercase">Homepage Hero</h3></div>
        <p className="text-white/40 text-xs mb-4">Edit the text shown over the home page banner. Emojis, numbers &amp; punctuation are all supported.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Season Title</label>
            <input value={hero.season_title} onChange={(e) => setHero((h) => ({ ...h, season_title: e.target.value }))} maxLength={60} placeholder="Season 2026" data-testid="hero-season-input" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Status Text</label>
            <input value={hero.status_text} onChange={(e) => setHero((h) => ({ ...h, status_text: e.target.value }))} maxLength={60} placeholder="Now Live" data-testid="hero-status-input" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all" />
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Tagline / Slogan</label>
          <textarea value={hero.tagline} onChange={(e) => setHero((h) => ({ ...h, tagline: e.target.value }))} maxLength={120} rows={2} placeholder="Where the city comes to ball." data-testid="hero-tagline-input" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all" />
        </div>

        {/* Overlay / background tint */}
        <div className="mb-5">
          <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Banner Overlay Color</label>
          <div className="flex items-center gap-3">
            <input type="color" value={hero.overlay_color || "#000000"} onChange={(e) => setHero((h) => ({ ...h, overlay_color: e.target.value }))} data-testid="hero-overlay-color-input" className="h-11 w-14 bg-transparent border border-white/15 cursor-pointer p-1" />
            <input value={hero.overlay_color || ""} onChange={(e) => setHero((h) => ({ ...h, overlay_color: e.target.value }))} placeholder="#000000" data-testid="hero-overlay-hex-input" className="w-32 bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all" />
            <span className="text-white/40 text-xs">Tints the banner behind the text so it stays readable. Use black for the classic look.</span>
          </div>
        </div>

        {/* Call-to-action buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div className="border border-white/10 p-4">
            <div className="text-[10px] uppercase tracking-wide text-[#FF5722] font-bold mb-3">Primary Button</div>
            <label className="block text-[10px] uppercase tracking-wide text-white/50 mb-1.5">Button Text</label>
            <input value={hero.primary_cta_text} onChange={(e) => setHero((h) => ({ ...h, primary_cta_text: e.target.value }))} maxLength={30} placeholder="Register Now" data-testid="hero-primary-cta-text" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all" />
            <label className="block text-[10px] uppercase tracking-wide text-white/50 mb-1.5">Link / URL</label>
            <input value={hero.primary_cta_link} onChange={(e) => setHero((h) => ({ ...h, primary_cta_link: e.target.value }))} placeholder="/register or https://..." data-testid="hero-primary-cta-link" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all" />
          </div>
          <div className="border border-white/10 p-4">
            <div className="text-[10px] uppercase tracking-wide text-white/60 font-bold mb-3">Secondary Button</div>
            <label className="block text-[10px] uppercase tracking-wide text-white/50 mb-1.5">Button Text</label>
            <input value={hero.secondary_cta_text} onChange={(e) => setHero((h) => ({ ...h, secondary_cta_text: e.target.value }))} maxLength={30} placeholder="View Schedule" data-testid="hero-secondary-cta-text" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all" />
            <label className="block text-[10px] uppercase tracking-wide text-white/50 mb-1.5">Link / URL</label>
            <input value={hero.secondary_cta_link} onChange={(e) => setHero((h) => ({ ...h, secondary_cta_link: e.target.value }))} placeholder="/schedule or https://..." data-testid="hero-secondary-cta-link" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all" />
          </div>
        </div>
        <p className="text-white/40 text-xs mb-4">Leave a button's text empty to hide it. Use a path like <span className="text-white/60">/register</span> for site pages or a full <span className="text-white/60">https://</span> link for external sites.</p>

        {/* Live preview */}
        <div className="border border-white/10 p-5 mb-4" style={{ backgroundColor: hero.overlay_color || "#000000" }} data-testid="hero-preview">
          <div className="text-[9px] uppercase tracking-wide text-white/40 mb-2">Live Preview</div>
          {[hero.season_title, hero.status_text].map((s) => s.trim()).filter(Boolean).length > 0 && (
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#FF5722] mb-2 font-semibold">
              {[hero.season_title, hero.status_text].map((s) => s.trim()).filter(Boolean).join(" · ")}
            </p>
          )}
          <div className="font-display text-3xl sm:text-4xl uppercase leading-[0.9] text-white">{hero.tagline.trim() || "Where the city comes to ball."}</div>
          <div className="flex flex-wrap gap-3 mt-4">
            {hero.primary_cta_text.trim() && <span className="bg-[#FF5722] text-black font-bold uppercase tracking-wide px-5 py-2 text-xs">{hero.primary_cta_text.trim()}</span>}
            {hero.secondary_cta_text.trim() && <span className="border border-white/40 text-white font-bold uppercase tracking-wide px-5 py-2 text-xs">{hero.secondary_cta_text.trim()}</span>}
          </div>
        </div>

        <button onClick={saveHero} disabled={savingHero} data-testid="hero-save-btn" className="bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide px-6 py-2.5 transition-all duration-200">
          {savingHero ? "Saving..." : "Save Changes"}
        </button>
      </div>

      {/* Wallpaper / Slideshow */}
      <div className="border border-white/10 bg-[#121212] p-6 mb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2"><ImageIcon size={20} className="text-[#FF5722]" /><h3 className="font-display text-2xl uppercase">Home Wallpaper</h3></div>
          <button
            onClick={() => setSlideshow((s) => !s)}
            data-testid="slideshow-toggle"
            className={`flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wide border transition-all duration-200 ${slideshow ? "bg-[#FF5722] text-black border-[#FF5722]" : "border-white/20 text-white/60 hover:text-white"}`}
          >
            <span className={`w-2 h-2 rounded-full ${slideshow ? "bg-black" : "bg-white/40"}`} />
            Slideshow {slideshow ? "On" : "Off"}
          </button>
        </div>

        <p className="text-white/40 text-xs mb-4">
          {images.length > 1 && slideshow ? "Images rotate automatically every 5 seconds on the home page." : "Add multiple images and turn on Slideshow to rotate them. The first image is used when slideshow is off."}
        </p>

        <div className="mb-4">
          <label className="block text-[10px] uppercase tracking-wide text-white/50 mb-2">Display Mode</label>
          <div className="flex gap-2">
            <button onClick={() => setHeroFit("cover")} data-testid="hero-fit-cover" className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wide border transition-all duration-200 ${heroFit === "cover" ? "bg-[#FF5722] text-black border-[#FF5722]" : "border-white/15 text-white/60 hover:text-white"}`}>
              Fill Screen (crop)
            </button>
            <button onClick={() => setHeroFit("contain")} data-testid="hero-fit-contain" className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wide border transition-all duration-200 ${heroFit === "contain" ? "bg-[#FF5722] text-black border-[#FF5722]" : "border-white/15 text-white/60 hover:text-white"}`}>
              Show Full (fit)
            </button>
          </div>
          <p className="text-white/40 text-xs mt-2">{heroFit === "contain" ? "Entire photo/video is visible (may show bars on the sides)." : "Media fills the hero and may be cropped at the edges."}</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4" data-testid="wallpaper-list">
          {images.map((img, i) => (
            <div key={`${img}-${i}`} className="relative group border border-white/10 overflow-hidden">
              <div className="aspect-video bg-black">
                {isVideoUrl(img)
                  ? <video src={resolveMedia(img)} muted loop playsInline className="w-full h-full object-cover" />
                  : <img src={resolveMedia(img)} alt={`wallpaper ${i + 1}`} className="w-full h-full object-cover" />}
              </div>
              {isVideoUrl(img) && <span className="absolute top-1 right-1 text-[9px] uppercase font-bold bg-black/70 text-white px-1.5 py-0.5">Video</span>}
              {i === 0 && <span className="absolute top-1 left-1 text-[9px] uppercase font-bold bg-[#FF5722] text-black px-1.5 py-0.5">Cover</span>}
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-black/70 px-1.5 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="flex gap-1">
                  <button onClick={() => moveImage(i, -1)} className="text-white/70 hover:text-[#FF5722] text-xs px-1" title="Move left">←</button>
                  <button onClick={() => moveImage(i, 1)} className="text-white/70 hover:text-[#FF5722] text-xs px-1" title="Move right">→</button>
                </div>
                <button onClick={() => removeImage(i)} data-testid="remove-wallpaper-btn" className="text-white/70 hover:text-[#FF3B30]"><Trash size={14} /></button>
              </div>
            </div>
          ))}
          {images.length === 0 && <p className="text-white/40 text-sm col-span-full">No media yet — add a photo or video below.</p>}
        </div>

        <label className="flex items-center gap-2 border border-dashed border-white/20 px-4 py-5 cursor-pointer hover:border-[#FF5722] transition-colors justify-center mb-3">
          <UploadSimple size={20} className="text-[#FF5722]" />
          <span className="text-sm text-white/60">{uploading ? "Uploading..." : "Upload a photo or video"}</span>
          <input type="file" accept="image/*,video/*" className="hidden" onChange={(e) => uploadFile(e.target.files[0])} data-testid="wallpaper-file-input" />
        </label>
        <div className="flex gap-2">
          <input value={newUrl} onChange={(e) => setNewUrl(e.target.value)} placeholder="...or paste a photo / video URL" data-testid="wallpaper-url-input" className="flex-1 bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all" />
          <button onClick={addUrl} data-testid="add-wallpaper-url-btn" className="border border-white/20 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 font-bold uppercase tracking-wide px-5 text-sm transition-all duration-200">Add</button>
        </div>
        <button onClick={saveWallpaper} disabled={savingWp} data-testid="save-wallpaper-btn" className="w-full mt-4 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide py-3 transition-all duration-200">
          {savingWp ? "Saving..." : "Save Wallpaper"}
        </button>
      </div>

      {/* Announcements */}
      <div className="border border-white/10 bg-[#121212] p-6 mb-6" data-testid="announcements-editor">
        <div className="flex items-center gap-2 mb-2"><Megaphone size={20} className="text-[#FF5722]" /><h3 className="font-display text-2xl uppercase">Announcements</h3></div>
        <p className="text-white/40 text-xs mb-4">Upload photos or videos to show in the Announcements box on the home page (right side). Add an optional caption for each.</p>

        <div className="space-y-3 mb-4" data-testid="announcements-list">
          {anns.map((a, i) => (
            <div key={`${a.url}-${i}`} className="flex gap-3 border border-white/10 p-3" data-testid="announcement-edit-row">
              <div className="w-28 h-20 bg-black shrink-0 overflow-hidden">
                {isVideoUrl(a.url)
                  ? <video src={resolveMedia(a.url)} muted className="w-full h-full object-cover" />
                  : <img src={resolveMedia(a.url)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1">
                <textarea value={a.caption} onChange={(e) => setAnnCaption(i, e.target.value)} placeholder="Caption (optional)" rows={2} data-testid={`announcement-caption-${i}`} className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all" />
                {isVideoUrl(a.url) && <span className="text-[10px] uppercase text-white/40">Video</span>}
              </div>
              <div className="flex flex-col items-center gap-1">
                <button onClick={() => moveAnn(i, -1)} className="text-white/60 hover:text-[#FF5722] text-xs" title="Move up">↑</button>
                <button onClick={() => moveAnn(i, 1)} className="text-white/60 hover:text-[#FF5722] text-xs" title="Move down">↓</button>
                <IconBtn onClick={() => removeAnn(i)} danger testid={`remove-announcement-${i}`}><Trash size={16} /></IconBtn>
              </div>
            </div>
          ))}
          {anns.length === 0 && <p className="text-white/40 text-sm">No announcements yet — upload a photo/video or add a URL.</p>}
        </div>

        <label className="flex items-center gap-2 border border-dashed border-white/20 px-4 py-5 cursor-pointer hover:border-[#FF5722] transition-colors justify-center mb-3">
          <UploadSimple size={20} className="text-[#FF5722]" />
          <span className="text-sm text-white/60">{uploadingAnn ? "Uploading..." : "Upload a photo or video"}</span>
          <input type="file" accept="image/*,video/*" className="hidden" onChange={(e) => uploadAnnFile(e.target.files[0])} data-testid="announcement-file-input" />
        </label>
        <div className="flex gap-2">
          <input value={annUrl} onChange={(e) => setAnnUrl(e.target.value)} placeholder="...or paste a photo / video URL" data-testid="announcement-url-input" className="flex-1 bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all" />
          <button onClick={addAnnUrl} data-testid="add-announcement-url-btn" className="border border-white/20 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 font-bold uppercase tracking-wide px-5 text-sm transition-all duration-200">Add</button>
        </div>
        <button onClick={saveAnnouncements} disabled={savingAnn} data-testid="save-announcements-btn" className="w-full mt-4 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide py-3 transition-all duration-200">
          {savingAnn ? "Saving..." : "Save Announcements"}
        </button>
      </div>

      {/* Background Music */}
      <div className="border border-white/10 bg-[#121212] p-6 mb-6" data-testid="music-editor">
        <div className="flex items-center gap-2 mb-2"><MusicNotes size={20} className="text-[#FF5722]" /><h3 className="font-display text-2xl uppercase">Background Music</h3></div>
        <p className="text-white/40 text-xs mb-4">Add up to {MAX_TRACKS} tracks to play in the background across the public site. They play one after another (and loop). A floating play button appears for visitors (browsers require a click before audio can start).</p>

        <label className="flex items-center justify-between gap-2 border border-white/10 px-4 py-3 mb-4 cursor-pointer hover:border-[#FF5722] transition-colors" data-testid="music-enabled-toggle">
          <span className="text-sm text-white/80">Enable background music</span>
          <input type="checkbox" checked={musicEnabled} onChange={(e) => setMusicEnabled(e.target.checked)} className="w-5 h-5 accent-[#FF5722]" data-testid="music-enabled-checkbox" />
        </label>

        <div className="space-y-2 mb-4" data-testid="music-tracks-list">
          {musicTracks.map((t, i) => (
            <div key={`${t}-${i}`} className="flex items-center gap-3 border border-white/10 p-3" data-testid="music-track-row">
              <span className="font-display text-lg text-[#FF5722] tabular-nums w-6 text-center shrink-0">{i + 1}</span>
              <MusicNotes size={20} className="text-[#FF5722] shrink-0" />
              <audio src={resolveMedia(t)} controls className="flex-1 h-9 min-w-0" data-testid={`music-preview-${i}`} />
              <IconBtn onClick={() => removeMusicTrack(i)} danger testid={`remove-music-track-${i}`}><Trash size={16} /></IconBtn>
            </div>
          ))}
          {musicTracks.length === 0 && <p className="text-white/40 text-sm">No tracks yet — upload a track or add an audio URL below.</p>}
          <p className="text-white/40 text-xs text-right">{musicTracks.length} / {MAX_TRACKS} tracks</p>
        </div>

        <label className={`flex items-center gap-2 border border-dashed border-white/20 px-4 py-5 transition-colors justify-center mb-3 ${musicTracks.length >= MAX_TRACKS ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:border-[#FF5722]"}`}>
          <UploadSimple size={20} className="text-[#FF5722]" />
          <span className="text-sm text-white/60">{uploadingMusic ? "Uploading..." : (musicTracks.length >= MAX_TRACKS ? `Track limit reached (${MAX_TRACKS})` : "Upload a music track (MP3, etc.)")}</span>
          <input type="file" accept="audio/*" disabled={musicTracks.length >= MAX_TRACKS} className="hidden" onChange={(e) => uploadMusicFile(e.target.files[0])} data-testid="music-file-input" />
        </label>
        <div className="flex gap-2">
          <input value={musicUrl} onChange={(e) => setMusicUrl(e.target.value)} placeholder="...or paste an audio URL" disabled={musicTracks.length >= MAX_TRACKS} data-testid="music-url-input" className="flex-1 bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all disabled:opacity-40" />
          <button onClick={addMusicUrl} disabled={musicTracks.length >= MAX_TRACKS} data-testid="add-music-url-btn" className="border border-white/20 hover:border-[#FF5722] hover:text-[#FF5722] disabled:opacity-40 text-white/70 font-bold uppercase tracking-wide px-5 text-sm transition-all duration-200">Add</button>
        </div>
        <button onClick={saveMusic} disabled={savingMusic} data-testid="save-music-btn" className="w-full mt-4 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide py-3 transition-all duration-200">
          {savingMusic ? "Saving..." : "Save Music"}
        </button>
      </div>

      {/* Payments (Zelle / Venmo) */}
      <div className="border border-white/10 bg-[#121212] p-6 mb-6" data-testid="payments-editor">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
          <div className="flex items-center gap-2"><CurrencyDollar size={20} className="text-[#FF5722]" /><h3 className="font-display text-2xl uppercase">Payments (Zelle / Venmo)</h3></div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={payments.enabled} onChange={(e) => setPayments((p) => ({ ...p, enabled: e.target.checked }))} className="w-5 h-5 accent-[#FF5722]" data-testid="payments-enabled-checkbox" />
            <span className="text-xs font-bold uppercase tracking-wide text-white/70">Show on Registration</span>
          </label>
        </div>
        <p className="text-white/40 text-xs mb-4">Display your Zelle / Venmo payment details (and optional QR codes) on the Registration page so players/teams can pay league fees.</p>

        <div className="mb-5">
          <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Instructions / Fees</label>
          <textarea value={payments.instructions} onChange={(e) => setPayments((p) => ({ ...p, instructions: e.target.value }))} rows={3} placeholder="e.g. $75 per player / $400 per team. Include your team name in the memo." data-testid="payments-instructions-input" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all" />
        </div>

        {["zelle", "venmo"].map((m) => (
          <div key={m} className="border border-white/10 p-4 mb-4" data-testid={`payments-${m}-editor`}>
            <label className="flex items-center justify-between gap-2 cursor-pointer select-none mb-3">
              <span className="font-display text-xl uppercase" style={{ color: m === "zelle" ? "#9b5cf0" : "#3399ff" }}>{m}</span>
              <span className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wide text-white/50">Enable</span>
                <input type="checkbox" checked={payments[m].enabled} onChange={(e) => setPayMethod(m, "enabled", e.target.checked)} className="w-5 h-5 accent-[#FF5722]" data-testid={`payments-${m}-enabled`} />
              </span>
            </label>
            <In label={m === "zelle" ? "Zelle email or phone" : "Venmo @handle"} value={payments[m].label} onChange={(e) => setPayMethod(m, "label", e.target.value)} data-testid={`payments-${m}-label`} />
            <div className="flex items-center gap-3 mt-3">
              {payments[m].qr_url && <img src={resolveMedia(payments[m].qr_url)} alt="QR" className="w-16 h-16 object-contain bg-white p-1" />}
              <label className="flex-1 flex items-center gap-2 border border-dashed border-white/20 px-4 py-3 cursor-pointer hover:border-[#FF5722] transition-colors justify-center">
                <UploadSimple size={18} className="text-[#FF5722]" />
                <span className="text-xs text-white/60">{uploadingQr === m ? "Uploading..." : (payments[m].qr_url ? "Replace QR code" : "Upload QR code (optional)")}</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadQr(m, e.target.files[0])} data-testid={`payments-${m}-qr-input`} />
              </label>
              {payments[m].qr_url && <IconBtn onClick={() => setPayMethod(m, "qr_url", "")} danger testid={`payments-${m}-qr-remove`}><Trash size={16} /></IconBtn>}
            </div>
          </div>
        ))}
        <button onClick={savePayments} disabled={savingPay} data-testid="save-payments-btn" className="w-full bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide py-3 transition-all duration-200">
          {savingPay ? "Saving..." : "Save Payment Settings"}
        </button>
      </div>

      {/* Social Media Links */}
      <div className="border border-white/10 bg-[#121212] p-6 mb-6" data-testid="social-editor">
        <div className="flex items-center gap-2 mb-2"><ShareNetwork size={20} className="text-[#FF5722]" /><h3 className="font-display text-2xl uppercase">Social Media</h3></div>
        <p className="text-white/40 text-xs mb-4">Add links to your Facebook page, Instagram, and TikTok. Icons appear in the footer on every page. Leave a field blank to hide that icon.</p>

        <div className="space-y-4">
          <div>
            <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5"><FacebookLogo size={16} weight="fill" className="text-[#1877F2]" /> Facebook Page URL</label>
            <input value={social.facebook} onChange={(e) => setSocial((s) => ({ ...s, facebook: e.target.value }))} placeholder="https://facebook.com/yourpage" data-testid="social-facebook-input" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all" />
          </div>
          <div>
            <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5"><InstagramLogo size={16} weight="fill" className="text-[#E1306C]" /> Instagram URL</label>
            <input value={social.instagram} onChange={(e) => setSocial((s) => ({ ...s, instagram: e.target.value }))} placeholder="https://instagram.com/yourhandle" data-testid="social-instagram-input" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all" />
          </div>
          <div>
            <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5"><TiktokLogo size={16} weight="fill" className="text-white" /> TikTok URL</label>
            <input value={social.tiktok} onChange={(e) => setSocial((s) => ({ ...s, tiktok: e.target.value }))} placeholder="https://tiktok.com/@yourhandle" data-testid="social-tiktok-input" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all" />
          </div>
        </div>
        <button onClick={saveSocial} disabled={savingSocial} data-testid="save-social-btn" className="w-full mt-5 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide py-3 transition-all duration-200">
          {savingSocial ? "Saving..." : "Save Social Links"}
        </button>
      </div>

      {/* Divisions */}
      <div className="border border-white/10 bg-[#121212] p-6 mb-6" data-testid="divisions-editor">
        <div className="flex items-center gap-2 mb-2"><Trophy size={20} className="text-[#FF5722]" /><h3 className="font-display text-2xl uppercase">Divisions</h3></div>
        <p className="text-white/40 text-xs mb-4">Add, rename, or remove league divisions. Renaming auto-migrates existing teams &amp; registrations. Removing a division leaves its teams unassigned — reassign them in the Teams tab.</p>
        <div className="space-y-2">
          {divisions.map((d, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="font-display text-lg text-[#FF5722] tabular-nums w-7 text-center">{i + 1}</span>
              <input
                value={d}
                onChange={(e) => setDivisions((prev) => prev.map((x, idx) => (idx === i ? e.target.value : x)))}
                data-testid={`division-input-${i}`}
                placeholder="Division name"
                className="flex-1 bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all"
              />
              <IconBtn onClick={() => requestDeleteDivision(i)} danger testid={`delete-division-${i}`}><Trash size={18} /></IconBtn>
            </div>
          ))}
          {divisions.length === 0 && <p className="text-white/40 text-sm">No divisions. Add one below.</p>}
        </div>
        <div className="flex items-center gap-3 mt-4">
          <button onClick={() => setDivisions((prev) => [...prev, ""])} data-testid="add-division-btn" className="flex items-center gap-2 border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 text-xs font-bold uppercase tracking-wide px-4 py-2.5 transition-all duration-200">
            <Plus size={16} /> Add Division
          </button>
          <button onClick={saveDivisions} disabled={savingDiv} data-testid="save-divisions-btn" className="bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide px-6 py-2.5 text-sm transition-all duration-200 ml-auto">
            {savingDiv ? "Saving..." : "Save Divisions"}
          </button>
        </div>
      </div>

      {/* Division Age Rules */}
      <div className="border border-white/10 bg-[#121212] p-6 mb-6" data-testid="division-ages-editor">
        <div className="flex items-center gap-2 mb-2"><Trophy size={20} className="text-[#FF5722]" /><h3 className="font-display text-2xl uppercase">Division Age Rules</h3></div>
        <p className="text-white/40 text-xs mb-4">Set the minimum and/or maximum age for each division. Players outside the range get a warning (they are not blocked). Leave blank for no limit — e.g. "35 Over" → Min 35, Max blank.</p>
        {(divisions || []).filter((d) => (d || "").trim()).length === 0 ? (
          <p className="text-white/40 text-sm">Add divisions above first.</p>
        ) : (
          <div className="space-y-2">
            {(divisions || []).filter((d) => (d || "").trim()).map((d) => {
              const name = d.trim();
              const r = divisionAges[name] || {};
              return (
                <div key={name} className="flex items-center gap-3" data-testid={`age-rule-row-${name.replace(/\s+/g, "-").toLowerCase()}`}>
                  <span className="font-semibold uppercase text-sm text-white/80 flex-1 truncate">{name}</span>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] uppercase text-white/40">Min</span>
                    <input type="number" min={0} value={r.min ?? ""} onChange={(e) => setAge(name, "min", e.target.value)} data-testid={`age-min-${name.replace(/\s+/g, "-").toLowerCase()}`} placeholder="—" className="w-20 bg-[#1A1A1A] border border-white/15 px-2 py-2 text-sm text-center tabular-nums focus:outline-none focus:ring-2 focus:ring-[#FF5722]" />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] uppercase text-white/40">Max</span>
                    <input type="number" min={0} value={r.max ?? ""} onChange={(e) => setAge(name, "max", e.target.value)} data-testid={`age-max-${name.replace(/\s+/g, "-").toLowerCase()}`} placeholder="—" className="w-20 bg-[#1A1A1A] border border-white/15 px-2 py-2 text-sm text-center tabular-nums focus:outline-none focus:ring-2 focus:ring-[#FF5722]" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex mt-4">
          <button onClick={saveAges} disabled={savingAges} data-testid="save-ages-btn" className="bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide px-6 py-2.5 text-sm transition-all duration-200 ml-auto">
            {savingAges ? "Saving..." : "Save Age Rules"}
          </button>
        </div>
      </div>

      {pendingDelete && (
        <Modal open onClose={() => setPendingDelete(null)} title="Remove Division?">
          <div data-testid="division-delete-modal" className="space-y-5">
            <p className="text-sm text-white/70 leading-relaxed">
              <span className="text-[#FF5722] font-bold uppercase" data-testid="division-delete-name">{pendingDelete.name}</span> still has{" "}
              <span className="text-white font-bold" data-testid="division-delete-count">{pendingDelete.count} team{pendingDelete.count === 1 ? "" : "s"}</span> assigned to it.
            </p>
            <p className="text-sm text-white/50 leading-relaxed">
              Removing it won't delete those teams, but they'll become <span className="text-white/80">unassigned</span> until you move them to another division in the Teams tab. Remember to click <span className="text-white/80 font-semibold">Save Divisions</span> to apply this change.
            </p>
            <div className="flex gap-3 pt-1">
              <button onClick={() => setPendingDelete(null)} data-testid="division-delete-cancel" className="flex-1 border border-white/20 text-white/70 hover:bg-white/5 font-bold uppercase tracking-wide py-2.5 text-sm transition-colors">Cancel</button>
              <button onClick={confirmDeleteDivision} data-testid="division-delete-confirm" className="flex-1 bg-[#FF3B30] hover:bg-[#FF564C] text-white font-bold uppercase tracking-wide py-2.5 text-sm transition-colors">Remove Anyway</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Roster Rules */}
      <div className="border border-white/10 bg-[#121212] p-6 mb-6" data-testid="roster-rules-editor">
        <div className="flex items-center gap-2 mb-2"><Trophy size={20} className="text-[#FF5722]" /><h3 className="font-display text-2xl uppercase">Roster Rules</h3></div>
        <p className="text-white/40 text-xs mb-4">Control how jersey numbers work across all team rosters. Jersey numbers can be 0–9000.</p>
        <label className="flex items-center justify-between gap-3 border border-white/10 px-4 py-3 cursor-pointer hover:border-[#FF5722]/50 transition-colors">
          <div>
            <div className="text-sm text-white/80 font-semibold">Allow duplicate jersey numbers</div>
            <div className="text-white/40 text-xs mt-0.5">When off, two players on the same team can't share a number.</div>
          </div>
          <input type="checkbox" checked={allowDupJerseys} disabled={savingDup} onChange={(e) => saveDupJerseys(e.target.checked)} className="w-5 h-5 accent-[#FF5722] shrink-0" data-testid="allow-duplicate-jerseys-checkbox" />
        </label>
      </div>

      {/* League Fee & Waiver */}
      <div className="border border-white/10 bg-[#121212] p-6 mb-6" data-testid="compliance-settings">
        <div className="flex items-center gap-2 mb-2"><CurrencyDollar size={20} className="text-[#FF5722]" /><h3 className="font-display text-2xl uppercase">League Fee &amp; Waiver</h3></div>
        <p className="text-white/40 text-xs mb-4">Set the default league fee charged to each player (overridable per player) and edit the waiver players must sign. Players must pay in full before signing and are only eligible to play once both are done.</p>
        <div className="mb-4 max-w-xs">
          <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Default League Fee (per player)</label>
          <div className="flex items-center gap-2">
            <span className="text-white/50 font-display text-xl">$</span>
            <input type="number" min={0} value={defaultFee} onChange={(e) => setDefaultFee(e.target.value)} placeholder="120" data-testid="default-fee-input" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all" />
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Waiver Text</label>
          <textarea value={waiverText} onChange={(e) => setWaiverText(e.target.value)} rows={10} data-testid="waiver-text-input" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all" />
          <p className="text-white/40 text-xs mt-1">Shown on the public /waiver page. Line breaks are preserved.</p>
        </div>
        <button onClick={saveCompliance} disabled={savingCompliance} data-testid="save-compliance-btn" className="bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide px-6 py-2.5 transition-all duration-200">
          {savingCompliance ? "Saving..." : "Save Fee & Waiver"}
        </button>
      </div>

      <AccountSection />
      <AdminUsersSection />
    </div>
  );
}

/* ---------- Account (username / password) ---------- */
function AccountSection() {
  const { admin, applyToken, logout } = useAuth();
  const navigate = useNavigate();
  const [newEmail, setNewEmail] = useState("");
  const [emailPw, setEmailPw] = useState("");
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState("");

  const changeEmail = async (e) => {
    e.preventDefault(); setBusy("email");
    try {
      const res = await api.post("/auth/change-email", { new_email: newEmail, current_password: emailPw });
      applyToken(res.data.token, res.data.user);
      toast.success("Username (email) updated");
      setNewEmail(""); setEmailPw("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { toast.error("New passwords do not match"); return; }
    setBusy("password");
    try {
      await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      toast.success("Password updated. Please sign in again.");
      setCurPw(""); setNewPw(""); setConfirmPw("");
      setTimeout(() => { logout(); navigate("/admin"); }, 1200);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const resetCreds = async () => {
    if (!window.confirm("Reset username and password back to the original defaults?")) return;
    setBusy("reset");
    try {
      await api.post("/auth/reset-credentials");
      toast.success("Credentials reset to defaults. Please sign in again.");
      setTimeout(() => { logout(); navigate("/admin"); }, 1200);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="border border-white/10 bg-[#121212] p-6 mt-10" data-testid="account-section">
      <div className="flex items-center gap-2 mb-1"><GearSix size={20} className="text-[#FF5722]" /><h3 className="font-display text-2xl uppercase">Admin Account</h3></div>
      <p className="text-white/40 text-sm mb-6">Signed in as <span className="text-white/70">{admin?.email}</span></p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Change email */}
        <form onSubmit={changeEmail} className="space-y-4">
          <h4 className="text-sm font-bold uppercase tracking-wide text-white/70">Change Username (Email)</h4>
          <In label="New Email" type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="new-email-input" />
          <In label="Current Password" type="password" required value={emailPw} onChange={(e) => setEmailPw(e.target.value)} data-testid="email-current-password-input" />
          <button type="submit" disabled={busy === "email"} data-testid="change-email-btn" className="w-full bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide py-3 transition-all duration-200">
            {busy === "email" ? "Updating..." : "Update Email"}
          </button>
        </form>

        {/* Change password */}
        <form onSubmit={changePassword} className="space-y-4">
          <h4 className="text-sm font-bold uppercase tracking-wide text-white/70">Change Password</h4>
          <In label="Current Password" type="password" required value={curPw} onChange={(e) => setCurPw(e.target.value)} data-testid="current-password-input" />
          <In label="New Password" type="password" required value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-password-input" />
          <In label="Confirm New Password" type="password" required value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} data-testid="confirm-password-input" />
          <button type="submit" disabled={busy === "password"} data-testid="change-password-btn" className="w-full bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide py-3 transition-all duration-200">
            {busy === "password" ? "Updating..." : "Update Password"}
          </button>
        </form>
      </div>

      <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between flex-wrap gap-3">
        <p className="text-white/40 text-sm">Reset username and password to the original defaults.</p>
        <button onClick={resetCreds} disabled={busy === "reset"} data-testid="reset-credentials-btn" className="border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 disabled:opacity-50 font-bold uppercase tracking-wide px-6 py-2.5 text-sm transition-colors">
          {busy === "reset" ? "Resetting..." : "Reset to Defaults"}
        </button>
      </div>
    </div>
  );
}

/* ---------- Admin Users (multi-admin management) ---------- */
function AdminUsersSection() {
  const { admin } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/auth/admins").then((r) => setAdmins(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addAdmin = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/admins", { email: form.email, password: form.password, name: form.name || "Admin" });
      toast.success("Admin added");
      setForm({ email: "", password: "", name: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const removeAdmin = async (id, email) => {
    if (!window.confirm(`Remove admin ${email}? They will lose access immediately.`)) return;
    try {
      await api.delete(`/auth/admins/${id}`);
      toast.success("Admin removed");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="border border-white/10 bg-[#121212] p-6 mt-10" data-testid="admin-users-section">
      <div className="flex items-center gap-2 mb-1"><UserCircle size={20} className="text-[#FF5722]" /><h3 className="font-display text-2xl uppercase">Admin Users</h3></div>
      <p className="text-white/40 text-sm mb-6">Add or remove league admins. All admins have full access. You can't remove yourself or the last remaining admin.</p>

      <div className="border border-white/10 mb-6" data-testid="admin-users-list">
        {admins.map((a) => (
          <div key={a.id} className="flex items-center justify-between px-4 py-3 border-b border-white/5 last:border-0" data-testid="admin-user-row">
            <div className="flex items-center gap-3">
              <UserCircle size={22} className="text-white/40" />
              <div>
                <div className="font-semibold text-sm">{a.name} {a.is_self && <span className="text-[10px] uppercase bg-[#FF5722]/20 text-[#FF5722] px-2 py-0.5 ml-1">You</span>}</div>
                <div className="text-white/40 text-xs">{a.email}</div>
              </div>
            </div>
            {!a.is_self && (
              <IconBtn onClick={() => removeAdmin(a.id, a.email)} danger testid={`remove-admin-${a.id}`}><Trash size={18} /></IconBtn>
            )}
          </div>
        ))}
        {admins.length === 0 && <p className="text-white/40 text-sm px-4 py-4">Loading admins…</p>}
      </div>

      <form onSubmit={addAdmin} className="space-y-4">
        <h4 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-white/70"><UserPlus size={18} className="text-[#FF5722]" /> Add New Admin</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <In label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-admin-name-input" />
          <In label="Email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-admin-email-input" />
        </div>
        <In label="Temporary Password (min 6 chars)" type="text" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-admin-password-input" />
        <p className="text-white/40 text-xs">Share this email + password with the new admin. They can change it from this page after logging in.</p>
        <button type="submit" disabled={busy} data-testid="add-admin-btn" className="w-full sm:w-auto bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide px-8 py-3 transition-all duration-200">
          {busy ? "Adding..." : "Add Admin"}
        </button>
      </form>
    </div>
  );
}

